import { useState, useEffect, useCallback, useRef } from 'react'
import {
  UsersRound, RefreshCw, Loader2, Calendar, Landmark, AlertCircle,
  CheckSquare, Square, ChevronDown, GraduationCap, Ban, CheckCircle2,
  ArrowRight, Search, X, Filter, Save,
  Phone, FileText, FileDown, Upload
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { studentsApi, classesApi } from '@/services/api'

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES LOCALSTORAGE — synchronisées avec DashboardHeader
// ═══════════════════════════════════════════════════════════════════════════════
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

// ─── Helpers de récupération localStorage ───
const getStoredSchoolId = (): number | null => {
  try {
    const sid = localStorage.getItem(SELECTED_SCHOOL_ID_KEY)
    if (sid) return Number(sid)
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) return Number(state.user.school_id)
    }
  } catch (e) { console.warn('getStoredSchoolId error:', e) }
  return null
}

const getStoredYearId = (): number | null => {
  try {
    const yid = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yid) return Number(yid)
  } catch (e) { console.warn('getStoredYearId error:', e) }
  return null
}

const getStoredYearLabel = (): string | null => {
  try { return localStorage.getItem(SELECTED_YEAR_LABEL_KEY) } catch (e) { return null }
}

interface Student {
  id: number
  matricule: string
  first_name: string
  last_name: string
  sex: string
  class_name?: string
  status: string
  school_id?: number
  academic_year_id?: number
}

interface ClassItem {
  id: number
  name: string
  abbreviation: string
}

export default function StudentBulkUpdatePage() {
  const { lang } = useLang()

  // ─── RÉCUPÉRATION DEPUIS LOCALSTORAGE ───
  const [schoolId, setSchoolId] = useState<number | null>(getStoredSchoolId)
  const [academicYearId, setAcademicYearId] = useState<number | null>(getStoredYearId)
  const [academicYearName, setAcademicYearName] = useState<string>(getStoredYearLabel() || '')

  const [students, setStudents] = useState<Student[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [showActions, setShowActions] = useState(false)
  const [bulkAction, setBulkAction] = useState<'class' | 'status' | ''>('')
  const [targetClassId, setTargetClassId] = useState('')
  const [targetStatus, setTargetStatus] = useState('')
  const [updating, setUpdating] = useState(false)

  // ─── Bulk contacts update states ───
  const [contactsModalOpen, setContactsModalOpen] = useState(false)
  const [contactsFile, setContactsFile] = useState<File | null>(null)
  const [contactsLoading, setContactsLoading] = useState(false)
  const [contactsClassId, setContactsClassId] = useState<string>('')

  // ─── REFS pour éviter les boucles infinies ───
  const schoolIdRef = useRef<number | null>(schoolId)
  const yearIdRef = useRef<number | null>(academicYearId)

  useEffect(() => { schoolIdRef.current = schoolId }, [schoolId])
  useEffect(() => { yearIdRef.current = academicYearId }, [academicYearId])

  const $t = {
    fr: {
      title: 'Mise à jour groupés',
      subtitle: 'Modifier plusieurs élèves simultanément',
      loading: 'Chargement...',
      error: 'Erreur de chargement',
      noData: 'Aucun élève',
      noDataDesc: 'Aucun élève trouvé pour cette année scolaire.',
      school: 'Établissement',
      academicYear: 'Année scolaire',
      noYear: 'Aucune année sélectionnée',
      noSchool: 'Aucun établissement sélectionné',
      search: 'Rechercher...',
      selectAll: 'Tout sélectionner',
      deselectAll: 'Tout désélectionner',
      selected: 'sélectionné(s)',
      actions: 'Actions groupées',
      changeClass: 'Changer de classe',
      changeStatus: 'Changer de statut',
      apply: 'Appliquer',
      cancel: 'Annuler',
      class: 'Classe',
      status: 'Statut',
      matricule: 'Matricule',
      name: 'Nom',
      sex: 'Sexe',
      currentClass: 'Classe actuelle',
      success: 'Mise à jour effectuée',
      errorUpdate: 'Erreur lors de la mise à jour',
      importContacts: 'Importer contacts',
      bulkContactsTitle: 'Mise à jour des contacts par lot',
      downloadTemplate: 'Télécharger le modèle',
      uploadContactsFile: 'Uploader le fichier rempli',
      bulkUpdateSuccess: 'Contacts mis à jour avec succès',
      bulkUpdateError: 'Erreur lors de la mise à jour des contacts',
      contactsTemplateHelp: 'Téléchargez le modèle, remplissez les contacts, puis uploadez le fichier.',
      classFilter: 'Classe (optionnel)',
      noFileSelected: 'Aucun fichier sélectionné',
      fileSelected: 'Fichier sélectionné',
    },
    en: {
      title: 'Bulk Update',
      subtitle: 'Update multiple students at once',
      loading: 'Loading...',
      error: 'Loading error',
      noData: 'No students',
      noDataDesc: 'No students found for this academic year.',
      school: 'School',
      academicYear: 'Academic Year',
      noYear: 'No year selected',
      noSchool: 'No school selected',
      search: 'Search...',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      selected: 'selected',
      actions: 'Bulk Actions',
      changeClass: 'Change class',
      changeStatus: 'Change status',
      apply: 'Apply',
      cancel: 'Cancel',
      class: 'Class',
      status: 'Status',
      matricule: 'Reg. No.',
      name: 'Name',
      sex: 'Sex',
      currentClass: 'Current class',
      success: 'Update completed',
      errorUpdate: 'Error during update',
      importContacts: 'Import contacts',
      bulkContactsTitle: 'Bulk contacts update',
      downloadTemplate: 'Download template',
      uploadContactsFile: 'Upload filled file',
      bulkUpdateSuccess: 'Contacts updated successfully',
      bulkUpdateError: 'Error updating contacts',
      contactsTemplateHelp: 'Download the template, fill in the contacts, then upload the file.',
      classFilter: 'Class (optional)',
      noFileSelected: 'No file selected',
      fileSelected: 'File selected',
    }
  }[lang]

  // ─── CHARGEMENT DES DONNÉES — exécuté UNE SEULE FOIS au montage ───
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const currentSchoolId = schoolIdRef.current
      const currentYearId = yearIdRef.current

      const params: Record<string, any> = {}
      if (currentSchoolId) params.school_id = currentSchoolId
      if (currentYearId) params.academic_year_id = currentYearId

      // Charger les élèves
      const res = await studentsApi.getAll(params)
      const items = res?.items || res?.data || res || []
      setStudents(Array.isArray(items) ? items : [])

      // Charger les classes
      const clsRes = await classesApi.getAll({ school_id: currentSchoolId, academic_year_id: currentYearId })
      const clsItems = clsRes?.items || clsRes?.data || clsRes || []
      setClasses(Array.isArray(clsItems) ? clsItems : [])
    } catch (err) {
      console.error('Erreur chargement:', err)
      toast.error($t.error)
    } finally {
      setLoading(false)
    }
  }, [$t.error])

  useEffect(() => { loadData() }, [loadData])

  // ─── ÉCOUTE DES CHANGEMENTS D'ANNÉE (CustomEvent + localStorage) ───
  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const customEvent = e as CustomEvent
      const detail = customEvent.detail
      if (!detail?.year_id) return

      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null
      const currentYearId = yearIdRef.current
      const currentSchoolId = schoolIdRef.current

      let changed = false

      if (newYearId && newYearId !== currentYearId) {
        setAcademicYearId(newYearId)
        yearIdRef.current = newYearId
        const label = detail.label || getStoredYearLabel() || ''
        setAcademicYearName(label)
        changed = true
      }

      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== currentSchoolId) {
        setSchoolId(newSchoolId)
        schoolIdRef.current = newSchoolId
        changed = true
      }

      if (changed) {
        setSelectedIds(new Set())
        setShowActions(false)
        setBulkAction('')
        toast.info(lang === 'fr' ? 'Contexte scolaire mis à jour — rechargement' : 'School context updated — reloading')
        loadData()
      }
    }

    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)

    const handleStorageChange = (e: StorageEvent) => {
      let changed = false

      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        const currentYearId = yearIdRef.current
        if (newYearId && newYearId !== currentYearId) {
          setAcademicYearId(newYearId)
          yearIdRef.current = newYearId
          const label = localStorage.getItem(SELECTED_YEAR_LABEL_KEY) || ''
          setAcademicYearName(label)
          changed = true
        }
      }
      if (e.key === SELECTED_SCHOOL_ID_KEY) {
        const newSchoolId = e.newValue ? Number(e.newValue) : null
        const currentSchoolId = schoolIdRef.current
        if (newSchoolId && newSchoolId !== currentSchoolId) {
          setSchoolId(newSchoolId)
          schoolIdRef.current = newSchoolId
          changed = true
        }
      }

      if (changed) {
        setSelectedIds(new Set())
        setShowActions(false)
        setBulkAction('')
        toast.info(lang === 'fr' ? 'Contexte scolaire mis à jour — rechargement' : 'School context updated — reloading')
        loadData()
      }
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [lang, loadData])

  // ─── FILTRAGE PAR RECHERCHE ───
  const filteredStudents = students.filter((s) => {
    const q = searchQuery.toLowerCase()
    return (
      s.matricule.toLowerCase().includes(q) ||
      s.first_name.toLowerCase().includes(q) ||
      s.last_name.toLowerCase().includes(q) ||
      (s.class_name || '').toLowerCase().includes(q)
    )
  })

  // ─── SÉLECTION ───
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredStudents.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredStudents.map(s => s.id)))
    }
  }

  // ─── ACTION GROUPÉE ───
  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) {
      toast.error(lang === 'fr' ? 'Aucun élève sélectionné' : 'No student selected')
      return
    }
    if (!bulkAction) {
      toast.error(lang === 'fr' ? 'Veuillez choisir une action' : 'Please select an action')
      return
    }
    if (bulkAction === 'class' && !targetClassId) {
      toast.error(lang === 'fr' ? 'Veuillez choisir une classe' : 'Please select a class')
      return
    }
    if (bulkAction === 'status' && !targetStatus) {
      toast.error(lang === 'fr' ? 'Veuillez choisir un statut' : 'Please select a status')
      return
    }

    setUpdating(true)
    try {
      const ids = Array.from(selectedIds)

      // Prefer a real bulk endpoint if provided at runtime, otherwise update each student.
      if ((studentsApi as any).bulkUpdate) {
        const payload: Record<string, any> = { student_ids: ids }
        if (bulkAction === 'class') payload.class_id = Number(targetClassId)
        if (bulkAction === 'status') payload.status = targetStatus
        await (studentsApi as any).bulkUpdate(payload)
      } else if ((studentsApi as any).update) {
        // If the API client exposes an update(id, data) method, call it for each student.
        await Promise.all(ids.map((id) => {
          const data: Record<string, any> = {}
          if (bulkAction === 'class') data.class_id = Number(targetClassId)
          if (bulkAction === 'status') data.status = targetStatus
          return (studentsApi as any).update(id, data)
        }))
      } else {
        // Last-resort fallback: issue PATCH requests directly to a conventional endpoint.
        await Promise.all(ids.map(async (id) => {
          const body: Record<string, any> = {}
          if (bulkAction === 'class') body.class_id = Number(targetClassId)
          if (bulkAction === 'status') body.status = targetStatus
          await fetch(`/api/students/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
        }))
      }

      toast.success(`${selectedIds.size} ${$t.selected} — ${$t.success}`)
      setSelectedIds(new Set())
      setShowActions(false)
      setBulkAction('')
      loadData()
    } catch (err) {
      console.error('Erreur mise à jour groupée:', err)
      toast.error($t.errorUpdate)
    } finally {
      setUpdating(false)
    }
  }

  // ─── Bulk contacts handlers ───
  const handleDownloadContactsTemplate = async () => {
    try {
      const params: Record<string, any> = { school_id: schoolId, academic_year_id: academicYearId }
      if (contactsClassId) params.class_id = Number(contactsClassId)
      const blob = await studentsApi.getContactsTemplate(params)
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.download = `modele_contacts_${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
      toast.success(lang === 'fr' ? 'Modèle téléchargé' : 'Template downloaded')
    } catch (err: any) {
      console.error('Template download error:', err)
      toast.error(lang === 'fr' ? 'Erreur téléchargement modèle' : 'Template download error')
    }
  }

  const handleContactsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner un fichier CSV' : 'Please select a CSV file')
      return
    }
    setContactsFile(file)
  }

  const handleImportContacts = async () => {
    if (!contactsFile) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner un fichier' : 'Please select a file')
      return
    }
    setContactsLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', contactsFile)
      const res = await studentsApi.importContacts(formData)
      if (res?.success) {
        toast.success(`${lang === 'fr' ? 'Contacts mis à jour' : 'Contacts updated'}: ${res.updated}`)
        setContactsModalOpen(false)
        setContactsFile(null)
        loadData()
      } else {
        toast.error(res?.detail || $t.bulkUpdateError)
      }
    } catch (err: any) {
      console.error('Import contacts error:', err)
      toast.error($t.bulkUpdateError)
    } finally {
      setContactsLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <h1 className="page-title flex items-center gap-2">
            <UsersRound className="w-6 h-6 text-orange-600" />
            {$t.title}
          </h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 text-orange-600 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* ═══════════════════════ HEADER ═══════════════════════ */}
      <div className="page-header">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <UsersRound className="w-6 h-6 text-orange-600" />
              {$t.title}
            </h1>
            <p className="page-subtitle">{$t.subtitle}</p>
            {/* ─── BADGE ÉTABLISSEMENT & ANNÉE ─── */}
            <div className="flex items-center gap-2 mt-1.5">
              {schoolId && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Landmark className="w-3 h-3" />
                  {$t.school} ID: {schoolId}
                </span>
              )}
              {academicYearId && academicYearName && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {academicYearName}
                </span>
              )}
              {!schoolId && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {$t.noSchool}
                </span>
              )}
              {schoolId && !academicYearId && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {$t.noYear}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setContactsModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              {lang === 'fr' ? 'Importer contacts' : 'Import contacts'}
            </button>
            {selectedIds.size > 0 && (
              <>
                <span className="px-3 py-1.5 bg-orange-100 text-orange-700 text-sm font-medium rounded-lg">
                  {selectedIds.size} {$t.selected}
                </span>
                <button
                  onClick={() => setShowActions(!showActions)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-all flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {$t.actions}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════════ BARRE D'ACTIONS GROUPÉES ═══════════════════════ */}
      {showActions && selectedIds.size > 0 && (
        <div className="mb-4 bg-white rounded-xl border border-orange-200 shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{$t.actions}</label>
              <select
                value={bulkAction}
                onChange={(e) => { setBulkAction(e.target.value as any); setTargetClassId(''); setTargetStatus('') }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
              >
                <option value="">{lang === 'fr' ? 'Choisir...' : 'Choose...'}</option>
                <option value="class">{$t.changeClass}</option>
                <option value="status">{$t.changeStatus}</option>
              </select>
            </div>

            {bulkAction === 'class' && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{$t.class}</label>
                <select
                  value={targetClassId}
                  onChange={(e) => setTargetClassId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                >
                  <option value="">{lang === 'fr' ? 'Choisir une classe...' : 'Choose a class...'}</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.abbreviation})</option>
                  ))}
                </select>
              </div>
            )}

            {bulkAction === 'status' && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{$t.status}</label>
                <select
                  value={targetStatus}
                  onChange={(e) => setTargetStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                >
                  <option value="">{lang === 'fr' ? 'Choisir un statut...' : 'Choose a status...'}</option>
                  <option value="active">{lang === 'fr' ? 'Actif' : 'Active'}</option>
                  <option value="inscrit">{lang === 'fr' ? 'Inscrit' : 'Enrolled'}</option>
                  <option value="nouveau">{lang === 'fr' ? 'Nouveau' : 'New'}</option>
                  <option value="exclu">{lang === 'fr' ? 'Exclu' : 'Excluded'}</option>
                  <option value="renvoye">{lang === 'fr' ? 'Renvoyé' : 'Expelled'}</option>
                  <option value="transfere">{lang === 'fr' ? 'Transféré' : 'Transferred'}</option>
                  <option value="demission">{lang === 'fr' ? 'Démission' : 'Resigned'}</option>
                </select>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkUpdate}
                disabled={updating || !bulkAction}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {$t.apply}
              </button>
              <button
                onClick={() => { setShowActions(false); setBulkAction(''); setTargetClassId(''); setTargetStatus('') }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                {$t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════ BARRE DE RECHERCHE ═══════════════════════ */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={$t.search}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ═══════════════════════ TABLEAU ═══════════════════════ */}
      {students.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-16">
            <RefreshCw className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {lang === 'fr' ? 'Mise à jour en masse' : 'Bulk Update'}
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {lang === 'fr'
                ? 'Cette page permet de modifier des informations communes pour plusieurs élèves en une seule opération (classe, statut, etc.).'
                : 'This page allows updating common information for multiple students in a single operation (class, status, etc.).'}
            </p>
            <p className="text-gray-400 text-sm mt-4">
              {$t.noDataDesc}
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase hover:text-orange-600 transition-colors"
                      >
                        {selectedIds.size === filteredStudents.length && filteredStudents.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-orange-600" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                        {selectedIds.size > 0 ? `${selectedIds.size} ${$t.selected}` : ''}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.matricule}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.name}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.sex}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.currentClass}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.status}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredStudents.map((student) => (
                    <tr
                      key={student.id}
                      className={`hover:bg-gray-50/50 transition-colors ${selectedIds.has(student.id) ? 'bg-orange-50/50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleSelect(student.id)}
                          className="flex items-center gap-2"
                        >
                          {selectedIds.has(student.id) ? (
                            <CheckSquare className="w-4 h-4 text-orange-600" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-blue-700 font-medium">{student.matricule}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-700 text-xs font-bold">
                            {student.first_name[0]}{student.last_name[0]}
                          </div>
                          <span className="font-medium text-gray-900">{student.last_name} {student.first_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          student.sex === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                        }`}>
                          {student.sex === 'M' ? '♂' : '♀'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{student.class_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          student.status === 'active' || student.status === 'inscrit' ? 'bg-green-100 text-green-700' :
                          student.status === 'renvoye' || student.status === 'exclu' ? 'bg-red-100 text-red-700' :
                          student.status === 'transfere' ? 'bg-cyan-100 text-cyan-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {student.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
              <span>{filteredStudents.length} / {students.length} {lang === 'fr' ? 'élève(s)' : 'student(s)'}</span>
              {selectedIds.size > 0 && (
                <span className="text-orange-600 font-medium">{selectedIds.size} {$t.selected}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL MISE À JOUR EN MASSE DES CONTACTS
          ═══════════════════════════════════════════════════════════ */}
      {contactsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setContactsModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white"><Phone className="w-5 h-5" /></div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{$t.bulkContactsTitle}</h3>
                  <p className="text-blue-100 text-sm">{lang === 'fr' ? 'Mise à jour par fichier CSV' : 'Update via CSV file'}</p>
                </div>
              </div>
              <button onClick={() => setContactsModalOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3">{$t.contactsTemplateHelp}</p>

              {/* Sélecteur de classe */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{$t.classFilter}</label>
                <select value={contactsClassId} onChange={(e) => setContactsClassId(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  <option value="">{lang === 'fr' ? 'Toutes les classes' : 'All classes'}</option>
                  {classes.map((c) => (<option key={c.id} value={c.id}>{c.name} ({c.abbreviation})</option>))}
                </select>
              </div>

              {/* Étape 1 : Télécharger le modèle */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><FileDown className="w-4 h-4 text-blue-600" />{$t.downloadTemplate}</h4>
                <p className="text-xs text-gray-500 mb-3">{lang === 'fr' ? 'Téléchargez le modèle pré-rempli avec les élèves de la classe sélectionnée.' : 'Download the pre-filled template with students from the selected class.'}</p>
                <button onClick={handleDownloadContactsTemplate} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 transition-all">
                  <FileText className="w-4 h-4" />{$t.downloadTemplate}
                </button>
              </div>

              {/* Étape 2 : Uploader le fichier rempli */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><Upload className="w-4 h-4 text-emerald-600" />{$t.uploadContactsFile}</h4>
                <p className="text-xs text-gray-500 mb-3">{lang === 'fr' ? 'Sélectionnez le fichier CSV rempli avec les nouveaux contacts.' : 'Select the filled CSV file with the new contacts.'}</p>
                <label className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-200 border-dashed rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer transition-all">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  {contactsFile ? contactsFile.name : (lang === 'fr' ? 'Choisir un fichier CSV...' : 'Choose a CSV file...')}
                  <input type="file" accept=".csv" onChange={handleContactsFileChange} className="hidden" />
                </label>
                {contactsFile && (
                  <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{contactsFile.name} — {(contactsFile.size / 1024).toFixed(1)} KB</p>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => { setContactsModalOpen(false); setContactsFile(null); }} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">{$t.cancel}</button>
              <button onClick={handleImportContacts} disabled={contactsLoading || !contactsFile} className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center gap-2">
                {contactsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {lang === 'fr' ? 'Mettre à jour' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}